import { transition } from '@helpers/animate';
import { imageCoverContainer, imageWrapperPreserveRatio } from '@helpers/aspectRatio';
import brand, { BrandColours } from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { Device, from, until } from '@helpers/media';
import ButtonStyles from '@stories/Components/Buttons/Button/Button.styles';
import CategoryTagsStyles from '@stories/Components/CategoryTags/CategoryTags.styles';
import HeadingSummaryBlockStyles from '@stories/Components/Misc/HeadingSummaryBlock/HeadingSummaryBlock.styles';
import SectionWrapper from '@stories/Components/Misc/SectionWrapper/SectionWrapper';
import SectionWrapperStyles from '@stories/Components/Misc/SectionWrapper/SectionWrapper.styles';
import { rgba } from 'polished';
import styled, { createGlobalStyle, css } from 'styled-components';
import CtaCardsStyles from '../CtaCards/CtaCards.styles';
import UspStyles from '../Usp/Usp.styles';
// Add scrolling background shapes to the Rich Text containers
const GlobalStyles = createGlobalStyle `
  :root  {
    @media ${from(Device.DesktopSmall)} {
      main::before,
      main::after {
        content: '';
        position: fixed;
        width: 25%;
        aspect-ratio: 1;
        border-radius: 90px;
        background-color: ${rgba(brand.primary.blue.light, 0.15)};
      }

      main::before {
        bottom: 0;
        left: 0;
        transform: rotate(20deg) translate(-33%, 50%);
      }

      main::after {
        top: 0;
        right: 0;
        transform: rotate(20deg) translate(33%, -33%);
      }

      // ensure all solid widgets sit above the background shapes
      ${SectionWrapperStyles.Container},
      ${UspStyles.Outer}, ${CtaCardsStyles.Outer} {
        position: relative;
        z-index: 1;
      }
    }
  }
`;
export const colourDefinitions = css `
  /* Primary Colours */
  &[data-primary=${BrandColours.BlueDark}] {
    --primary: ${brand.primary.blue.dark};
  }

  &[data-primary=${BrandColours.BlueLight}] {
    --primary: ${brand.primary.blue.light};
  }

  &[data-primary=${BrandColours.GreenDark}] {
    --primary: ${brand.secondary.green.dark};
  }

  &[data-primary=${BrandColours.GreenLight}] {
    --primary: ${brand.secondary.green.light};
  }

  &[data-primary=${BrandColours.GreyPrimary}] {
    --primary: ${brand.primary.grey};
  }

  &[data-primary=${BrandColours.OrangeDark}] {
    --primary: ${brand.secondary.orange.dark};
  }

  &[data-primary=${BrandColours.OrangeLight}] {
    --primary: ${brand.secondary.orange.light};
  }

  &[data-primary=${BrandColours.PinkDark}] {
    --primary: ${brand.secondary.pink.dark};
  }

  &[data-primary=${BrandColours.PinkLight}] {
    --primary: ${brand.secondary.pink.light};
  }

  &[data-primary=${BrandColours.PurpleDark}] {
    --primary: ${brand.primary.purple.dark};
  }

  &[data-primary=${BrandColours.PurpleLight}] {
    --primary: ${brand.primary.purple.light};
  }

  &[data-primary=${BrandColours.White}] {
    --primary: ${brand.global.white};
  }

  /* Secondary Colours */
  &[data-secondary=${BrandColours.BlueDark}] {
    --secondary: ${brand.primary.blue.dark};
  }

  &[data-secondary=${BrandColours.BlueLight}] {
    --secondary: ${brand.primary.blue.light};
  }

  &[data-secondary=${BrandColours.GreenDark}] {
    --secondary: ${brand.secondary.green.dark};
  }

  &[data-secondary=${BrandColours.GreenLight}] {
    --secondary: ${brand.secondary.green.light};
  }

  &[data-secondary=${BrandColours.GreyPrimary}] {
    --secondary: ${brand.primary.grey};
  }

  &[data-secondary=${BrandColours.OrangeDark}] {
    --secondary: ${brand.secondary.orange.dark};
  }

  &[data-secondary=${BrandColours.OrangeLight}] {
    --secondary: ${brand.secondary.orange.light};
  }

  &[data-secondary=${BrandColours.PinkDark}] {
    --secondary: ${brand.secondary.pink.dark};
  }

  &[data-secondary=${BrandColours.PinkLight}] {
    --secondary: ${brand.secondary.pink.light};
  }

  &[data-secondary=${BrandColours.PurpleDark}] {
    --secondary: ${brand.primary.purple.dark};
  }

  &[data-secondary=${BrandColours.PurpleLight}] {
    --secondary: ${brand.primary.purple.light};
  }

  &[data-secondary=${BrandColours.White}] {
    --secondary: ${brand.global.white};
  }
`;
const Container = styled(SectionWrapper) `
  ${colourDefinitions};

  --primary: ${brand.primary.purple.light};
  --secondary: ${brand.global.white};

  --sectionBackground: transparent;
  --sectionColour: ${brand.global.white};
  --paddingY: ${fluid(40, 68)};
  --rowGap: ${fluid(24, 48)};

  --customBaseMargin: calc(200px + var(--paddingY));

  margin-bottom: var(--customBaseMargin);

  position: relative;
  z-index: 1; // sit above the background shapes

  @media print {
    margin: 0;
  }

  &[data-layout='event'] {
    @media ${from(Device.TabletLarge)} {
      --customBaseMargin: calc(387px + var(--paddingY));
    }
  }

  @media ${until(Device.TabletLarge)} {
    --customBaseMargin: calc(72px + var(--paddingY));
  }

  &[data-secondary=${BrandColours.White}],
  &[data-secondary=${BrandColours.GreyPrimary}] {
    --sectionColour: ${brand.primary.blue.dark};
  }

  &[data-has-image='false'],
  &[data-image-position='aside'] {
    --customBaseMargin: var(--rowGap);
  }

  &[data-has-image='false'][data-secondary=${BrandColours.White}] {
    @media ${until(Device.TabletLarge)} {
      padding-bottom: 160px;
    }
  }

  ${SectionWrapperStyles.Inner} {
    display: grid;
    gap: var(--rowGap);
    padding-top: var(--paddingY);
    padding-bottom: var(--paddingY);

    @media print {
      padding: 0;
    }

    @media ${from(Device.TabletLarge)} {
      min-height: 513px;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
    }
  }

  &[data-image-position='aside'] ${SectionWrapperStyles.Inner} {
    @media ${from(Device.TabletLarge)} {
      flex-direction: row;
      place-items: flex-start;
      min-height: ${fluid(360, 513, Device.TabletLarge, Device.ActualDesktop)};

      ${() => ImageWrapper} {
        max-width: 56%;
      }

      ${HeadingSummaryBlockStyles.Container} {
        width: 100%;
      }
    }
  }

  &[data-animate='false'] {
    &::before {
      opacity: 0;
    }

    ${CategoryTagsStyles.Container} {
      opacity: 0;
      transform: translateY(20px);

      @media print {
        opacity: 1 !important;
        transform: translateY(0px) !important;
      }
    }
  }

  ${HeadingSummaryBlockStyles.Container} {
    transition-delay: 0.25s;

    @media ${until(Device.TabletLarge)} {
      grid-row: 1 / span 1;
      grid-column: 1 / span 1;
    }

    @media ${from(Device.TabletLarge)} {
      width: 50%;
      max-width: 840px;
    }

    ${ButtonStyles.Container} {
      --colourTextHover: var(--primary) !important;
    }
  }

  ${HeadingSummaryBlockStyles.Container}[data-animate='false'] {
    opacity: 0;
    transform: translateY(20px);

    @media print {
      opacity: 1 !important;
      transform: translateY(0px) !important;
    }
  }

  ${HeadingSummaryBlockStyles.Content},
  ${HeadingSummaryBlockStyles.Heading},
  ${HeadingSummaryBlockStyles.Subheading} {
    color: var(--sectionColour);
  }

  ${HeadingSummaryBlockStyles.Content} {
    p {
      font-size: ${fluid(14, 18)};
      line-height: ${fluid(22, 30)};
    }

    @media ${from(Device.TabletLarge)} {
      max-width: 695px;
    }
  }

  ${HeadingSummaryBlockStyles.ButtonWrapper} {
    gap: ${fluid(12, 16)};
    margin-top: 14px;
  }

  @media print {
    color: ${brand.global.black};
    --sectionColour: ${brand.global.black};
  }

  ${HeadingSummaryBlockStyles.DetailContent} {
    transition-delay: 0.35s;
  }

  ${HeadingSummaryBlockStyles.Container},
  ${CategoryTagsStyles.Container} {
    ${transition('transform, opacity', 0.5)};
  }

  ${CategoryTagsStyles.Container} {
    margin-bottom: ${fluid(12, 16)};
  }

  &[data-secondary=${BrandColours.BlueLight}] ${CategoryTagsStyles.Tag}[data-theme=${BrandColours.BlueLight}] {
    --tagColour: ${brand.primary.blue.dark};
    --textColour: ${brand.global.white};
  }
`;
const BannerContent = styled.div ``;
const AuthorList = styled.div `
  ${transition('transform, opacity', 0.5)};
  transition-delay: 0.5s;
  display: flex;
  gap: ${fluid(16, 40)};
  margin-top: ${fluid(16, 26)};

  [data-animate='false'] & {
    opacity: 0;
    transform: translateY(20px);
  }

  [data-image-position='aside'] & {
    display: grid;
    grid-template-columns: 1fr 1fr;

    @media ${until(Device.TabletLarge)} {
      display: flex;
    }
  }

  @media ${until(Device.TabletLarge)} {
    flex-direction: column;
  }
`;
const AuthorImage = styled.img `
  width: ${fluid(48, 68)};
  height: ${fluid(48, 68)};
  border-radius: 50%;
  border: 1px solid ${brand.global.white};

  [data-secondary=${BrandColours.White}] & {
    border-color: ${brand.primary.blue.dark};
  }
`;
const AuthorItem = styled.div `
  display: grid;
  gap: ${fluid(8, 15)};
  align-items: center;

  &:has(${AuthorImage}) {
    grid-template-columns: ${fluid(48, 68)} auto;
  }
`;
const AuthorContent = styled.div `
  ${fonts.lato.semiBold};
  font-size: ${fluid(15, 16)};
  line-height: ${fluid(23, 26)};
  display: flex;
  flex-direction: column;
`;
const AuthorName = styled.span ``;
const AuthorRole = styled.span ``;
const ImageWrapper = styled.div `
  ${imageWrapperPreserveRatio(344, 200)};
  margin-bottom: calc(var(--customBaseMargin) * -1);
  border-radius: ${fluid(10, 20)};

  @media ${until(Device.TabletLarge)} {
    grid-row: 2 / span 1;
    grid-column: 1 / span 1;
  }

  @media ${from(Device.TabletLarge)} {
    aspect-ratio: 1742 / 647;
  }

  @media print {
    width: 60%;
    margin: 0 auto;
  }
`;
const Image = styled.img `
  ${transition('opacity', 0.5)};
  ${imageCoverContainer()};

  transition-delay: 0.15s;

  ${Container}[data-animate='false'] & {
    opacity: 0;
  }
`;
const BackGroundHolder = styled.div `
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;
const PrimaryBackground = styled.div `
  ${transition('transform', 0.5)};

  --yAdjust: ${fluid(64, 146, Device.MobileLarge, Device.TabletLarge)};

  grid-row: 2 / span 1;
  grid-column: 1 / span 1;
  position: absolute;
  top: 90%;
  left: -10%;
  z-index: -1;
  background-color: var(--primary);
  width: 150%;
  height: 150%;
  border-radius: 50px;
  transform: rotate(-15deg);
  transform-origin: top left;
  margin: 0;

  ${Container}[data-primary=${BrandColours.BlueDark}] & {
    --primary: ${brand.primary.blue.dark};
  }

  ${Container}[data-animate='false'] & {
    transform: rotate(0) !important;
  }

  @media ${until(Device.TabletLarge)} {
    ${Container}[data-has-image='false'] & {
      top: calc(100% - 160px);
      right: auto;
      left: 100%;
      bottom: auto;
      transform: rotate(-15deg);
      transform-origin: bottom left;
      transition-duration: 650ms;
      margin: 0;
      border-radius: 93px;
    }

    ${Container}[data-has-image='false'][data-secondary=${BrandColours.White}] & {
      top: calc(100% - 160px);
      right: 0;
      left: auto;
      bottom: auto;
      transform: rotate(-9deg);
      transform-origin: top right;
      transition-duration: 650ms;
      margin: 0;
      border-radius: 93px;
    }
  }

  @media ${until(Device.Tablet)} {
    ${Container}[data-has-image='false'][data-secondary=${BrandColours.White}] & {
      top: calc(100% - 160px);
      right: 8%;
      left: auto;
      bottom: auto;
      transform: rotate(-15deg);
      transform-origin: top right;
      transition-duration: 650ms;
      margin: 0;
      border-radius: 93px;
    }
  }

  @media ${from(Device.TabletLarge)} {
    position: absolute;
    top: 0;
    right: -20%;
    left: auto;
    bottom: auto;
    border-radius: 0 0 66px 66px;
    transform: rotate(24deg);
    transform-origin: bottom right;
    width: ${fluid(800, 1200)};
    height: ${fluid(600, 800)};
    transition-duration: 650ms;
    margin: 0;
  }
`;
const SecondaryBackground = styled.div `
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: -2;
  background-color: var(--secondary);
`;
const ContentPageHeroStyles = {
    AuthorContent,
    AuthorImage,
    AuthorItem,
    AuthorList,
    AuthorName,
    AuthorRole,
    BackGroundHolder,
    BannerContent,
    Container,
    Image,
    ImageWrapper,
    PrimaryBackground,
    SecondaryBackground,
    GlobalStyles,
};
export default ContentPageHeroStyles;
